import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import type { BattleState } from '@somnhold/sim';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** A wave (three crests): the sea's row. */
const WAVE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 10c1.6 0 1.6-2 3.3-2s1.7 2 3.4 2 1.6-2 3.3-2 1.7 2 3.3 2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M1 13.5c1.6 0 1.6-2 3.3-2s1.7 2 3.4 2 1.6-2 3.3-2 1.7 2 3.3 2" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" opacity="0.6"/></svg>';
/** A stone of the canals, round, sitting in a groove. */
const STONE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 12.5h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" opacity="0.6"/><ellipse cx="8" cy="8.5" rx="4.6" ry="3.6" fill="currentColor"/></svg>';
/** An open eye (the altar's). */
const EYE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8C3.4 4.8 5.6 3.6 8 3.6S12.6 4.8 14.5 8C12.6 11.2 10.4 12.4 8 12.4S3.4 11.2 1.5 8z" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="8" r="2.2" fill="currentColor"/></svg>';

/** Where the Way of the Altars hangs its marks (render's AltarsLayer anchors, world points). */
export interface AltarAnchors {
  stones: THREE.Vector3[];
  pools: THREE.Vector3[];
  altars: THREE.Vector3[];
}

/**
 * The Way of the Altars on screen (docs/sendas/paladin.md, chapter 1), inside a path's own plate (game/senda-hud.ts)
 * and over the board, short enough that a phone's plate stays under a quarter of the screen:
 *
 * - the canals: the next wave (great or small in the test) counting down, «¡Viene el agua!» while the sea rolls in,
 *   «Se perdió el agua» a moment when it spilt; each pool's two waves as pips (silver), its eye open; in the test the
 *   round and the sea dropping below the high pool or going for good, red as it nears. Over the board, a mark on each
 *   stone, tappable (the hero goes and moves it), its ring filling as it works; the high pool's mouth reads open or
 *   shut.
 * - the second altar: the drake's next sweep counting down, red and pulsing while its cone is marked («¡El cono!
 *   Fuera, o al lado de Marit»); the altar's own life is in the people's list, its kindling in the plate's bar.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md).
 */
export class AltarHud {
  readonly panel: HTMLElement;
  private kind: 'canal' | 'kindle';
  private v = new THREE.Vector3();
  private wave = h('span.al-wave');
  private waveRow = h('div.bh-row.al-waverow');
  private pools: { row: HTMLElement; pips: HTMLElement[]; eye: HTMLElement }[] = [];
  private round = h('span.al-round');
  private sea = h('span.al-sea');
  private seaRow = h('div.bh-row.al-searow');
  private marks: { el: HTMLButtonElement; state: HTMLElement | null; mouth: boolean }[] = [];
  private drake = h('span.al-wave');
  private drakeRow = h('div.bh-row.al-waverow');
  private spillUntil = 0;
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    this.kind = s.rules?.senda?.kindle ? 'kindle' : 'canal';
    this.panel = this.kind === 'kindle' ? this.buildKindle() : this.buildCanal(s);
  }

  /* ---------- the canals ---------- */

  private buildCanal(s: BattleState): HTMLElement {
    const r = s.rules?.senda?.canal;
    if (!r) return h('div');
    this.waveRow.append(h('span.bh-ico', { html: WAVE }), h('span.bh-label', {}, t('senda.canal.waves')), this.wave);
    const rows: HTMLElement[] = [];
    r.pools.forEach((p) => {
      const pips = Array.from({ length: r.fill }, () => h('i'));
      const eye = h('span.al-eye', { html: EYE });
      const row = h('div.bh-row.al-pool', {}, eye, h('span.bh-label', {}, r.pools.length > 1 ? t(p.high ? 'senda.canal.high' : 'senda.canal.low') : t('senda.canal.pool')), h('span.al-pips', {}, ...pips));
      this.pools.push({ row, pips, eye });
      rows.push(row);
    });
    if (r.ebb) {
      this.seaRow.append(h('span.bh-ico', { html: ICONS.hourglass }), this.round, this.sea);
      rows.push(this.seaRow);
    }
    // The stones' marks over the board.
    this.marks = r.stones.map((st, i) => {
      const mouth = st.mouth >= 0;
      const state = mouth ? h('small.al-state') : null;
      const el = h(
        `button.al-stone${mouth ? '.mouth' : ''}`,
        { type: 'button', 'data-test': `canal-stone-${i}`, 'aria-label': t(mouth ? 'senda.canal.mouthHint' : 'senda.canal.stoneHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(i)) },
        h('span.al-ring'),
        h('span.al-glyph', { html: mouth ? ICONS.lock : STONE }),
        ...(state ? [state] : []),
      ) as HTMLButtonElement;
      this.board.append(el);
      return { el, state, mouth };
    });
    return h('div.bh.al-canal', {}, this.waveRow, ...rows);
  }

  private updateCanal(s: BattleState): void {
    const r = s.rules?.senda?.canal;
    const w = s.senda?.canal;
    if (!r || !w) return;
    for (const e of s._events ?? []) if (e.t === 'senda' && e.kind === 'spill' && (e.n ?? 0) >= 0) this.spillUntil = performance.now() + 2200;
    const T = s.waveT;
    const left = w.next >= 0 ? Math.max(0, Math.ceil((w.next - T) / TICK_HZ)) : -1;
    const spilt = performance.now() < this.spillUntil;
    const coming = w.next >= 0 && w.next - T <= r.warn && w.next - T > 0;
    const text = spilt ? t('senda.canal.spill') : w.front ? t('senda.canal.running') : coming ? t('senda.canal.coming') : left >= 0 ? t(r.ebb ? (w.big ? 'senda.canal.bigIn' : 'senda.canal.smallIn') : 'senda.canal.waveIn', { s: left }) : '';
    if (this.wave.textContent !== text) this.wave.textContent = text;
    this.waveRow.classList.toggle('warn', coming);
    this.waveRow.classList.toggle('lost', spilt);
    const key = `${w.level.join('')}|${w.pos.join('')}|${w.lit}|${w.failed}|${w.round}`;
    if (key !== this.key) {
      this.key = key;
      this.pools.forEach((p, i) => {
        const n = w.level[i] ?? 0;
        p.pips.forEach((x, j) => x.classList.toggle('on', j < n));
        const open = w.lit >= 0 || (r.kind === 'small' && n >= r.fill);
        p.row.classList.toggle('full', n >= r.fill);
        p.eye.classList.toggle('open', open);
      });
      this.marks.forEach((m, i) => {
        if (!m.mouth || !m.state) return;
        const shut = w.pos[i] === 1;
        m.el.classList.toggle('shut', shut);
        m.state.textContent = t(shut ? 'senda.canal.shut' : 'senda.canal.open');
      });
    }
    if (r.ebb) {
      const round = t('senda.canal.round', { n: w.round + 1, of: r.ebb.rounds });
      if (this.round.textContent !== round) this.round.textContent = round;
      const drop = w.drop >= 0 ? Math.max(0, Math.ceil((w.drop - T) / TICK_HZ)) : -1;
      const ebb = w.ebb >= 0 ? Math.max(0, Math.ceil((w.ebb - T) / TICK_HZ)) : -1;
      const sea = w.check >= 0 ? t('senda.canal.going') : drop >= 0 ? t('senda.canal.dropIn', { s: drop }) : ebb >= 0 ? t('senda.canal.ebbIn', { s: ebb }) : '';
      if (this.sea.textContent !== sea) this.sea.textContent = sea;
      this.seaRow.classList.toggle('warn', (drop >= 0 && drop <= r.ebb.warn / TICK_HZ) || (ebb >= 0 && ebb <= r.ebb.warn / TICK_HZ) || w.check >= 0);
    }
    this.marks.forEach((m, i) => {
      const sent = w.job === i;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.stand)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    });
  }

  /* ---------- the second altar ---------- */

  private buildKindle(): HTMLElement {
    this.drakeRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.kindle.drake')), this.drake);
    this.drakeRow.hidden = true;
    return h('div.bh.al-kindle', {}, this.drakeRow);
  }

  private updateKindle(s: BattleState): void {
    const r = s.rules?.senda?.kindle;
    const w = s.senda?.kindle;
    if (!r || !w) return;
    const d = w.drake ? s.enemies.find((e) => e.id === w.drake && e.hp > 0) : undefined;
    this.drakeRow.hidden = !d;
    if (!d) return;
    const left = Math.max(0, Math.ceil((w.next - s.waveT) / TICK_HZ));
    const text = w.marked ? t('senda.kindle.cone') : t('senda.kindle.coneIn', { s: left });
    if (this.drake.textContent !== text) this.drake.textContent = text;
    this.drakeRow.classList.toggle('warn', w.marked);
  }

  update(s: BattleState): void {
    if (this.kind === 'canal') this.updateCanal(s);
    else this.updateKindle(s);
  }

  place(camera: THREE.Camera, w: number, ht: number, a: AltarAnchors | null): void {
    if (!a || this.kind !== 'canal') return;
    this.marks.forEach((m, i) => {
      const p = a.stones[i];
      if (!p) return setStyle(m.el, 'display', 'none');
      this.v.copy(p).project(camera);
      if (this.v.z > 1) return setStyle(m.el, 'display', 'none');
      setStyle(m.el, 'display', '');
      pinMark(m.el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
    });
  }
}
